/**
 * CatcherPage - Main catcher station interface
 *
 * Follows Frontend UI Standards:
 * - Container with animate-enter
 * - Page header with icon box pattern
 * - MetricCards with proper variants
 * - Large touch targets for warehouse gloves
 * - Proper error state handling
 * - ARIA live regions for accessibility
 *
 * Post-accept placement overlay:
 * - Assigned containers appear immediately; completion waits for the audited accept
 * - Operator scans bin/shelf QR or clicks manual button to dismiss
 * - No server call needed for bin confirmation — validation is client-side only
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/features/production-orders/components/metrics/MetricCard';
import { QRScannerInput } from './QRScannerInput';
import { GarmentCard } from './GarmentCard';
import { cleanGarmentDisplayName } from '@/lib/garment-display-name';
import { ProductionImageLightbox } from '@/components/production/ProductionImageLightbox';
import {
  EMPTY_PRODUCTION_LIGHTBOX_DATA,
  buildFinishedGarmentPreviewEntries,
  buildProductionLightboxData,
  productionPreviewKey,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useProductionLightbox } from '@/components/production/useProductionLightbox';
import { BinDirective } from './BinDirective';
import { PlacementOverlay } from './PlacementOverlay';
import { PrePrintOverlay } from './PrePrintOverlay';
import { RedoReasonDialog } from '@/components/production/RedoReasonDialog';
import { DevQRTestPanel } from './DevQRTestPanel';
import { useGarmentLookup } from '../hooks/useGarmentLookup';
import {
  useCatcherMutations,
  type RedoReason,
  type AcceptGarmentResult,
} from '../hooks/useCatcherMutations';
import { useCatcherPlacement, type AcceptedPlacement, type PlacementDestination } from '../hooks/useCatcherPlacement';
import { useCatcherStats } from '../hooks/useCatcherStats';
import { useRecentlyCaught } from '../hooks/useRecentlyCaught';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { SupersededStickerDialog } from '@/components/production/SupersededStickerDialog';
import { parseRedoError, parseBenchError, parseOrderOnHold, parseSupersededError, getUserMessage } from '@/lib/trpc-error-utils';
import { formatOrderNumber } from '@threadheads/shared';
import { playErrorBeep, playSuccessBeep } from '@/lib/sounds';
import { detectQRType, extractGarmentId, extractBenchLabel } from '@/lib/qr-utils';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { Package, RefreshCw, Clock, Shirt, Undo2, ArrowLeft, Loader2, ScanLine, Plus } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useHotkeyEngine } from '@/features/production-settings/hotkeys/useHotkeyEngine';
import { useHotkeySettings } from '@/features/production-settings/hotkeys/useHotkeySettings';
import { formatBindingLabel } from '@/features/production-settings/hotkeys/hotkey-types';

export function CatcherPage() {
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const { t } = useTranslation('production');
  const [showRedoDialog, setShowRedoDialog] = useState(false);

  // Pre-print overlay state (shown INSTEAD of PlacementOverlay for pre-print batches)
  const [showPrePrintOverlay, setShowPrePrintOverlay] = useState(false);

  // Store last scanned QR code for re-fetch after undo
  const lastQrCodeRef = useRef<string | null>(null);

  // Scan feedback (modal-based errors)
  const feedback = useScanFeedback();

  // Garment lookup (imperative via utils.fetch)
  const {
    data: garmentData,
    error: lookupError,
    isLoading: isLookingUp,
    lookup: lookupGarment,
    clear: clearGarment,
  } = useGarmentLookup();

  // Garment-wide image lightbox. Declared here, with the other focus-trapping
  // overlays and ABOVE the scanner/hotkey pause lists that read it: a trapped
  // overlay has to pause the global scanner refocus (PRODUCTION_DESIGN §6.2),
  // and hooks must run in the same order on every render.
  //
  // ONE FLAT LIST for the garment on screen, built from the SAME pure builder
  // GarmentCard renders from, so a click opens that exact image and the arrows
  // ride this garment's other side. Everything else about the overlay's state
  // (the clicked tile's fallthrough, the empty-list guard, the reset on scan
  // away) is `useProductionLightbox`, shared with the other stations.
  const previewGarment = garmentData?.garmentUnit ?? null;
  const lightboxData = useMemo(
    () =>
      previewGarment
        ? buildProductionLightboxData([
            {
              subjectId: previewGarment.id,
              subject: cleanGarmentDisplayName(
                previewGarment.productName,
                previewGarment.color,
                previewGarment.size,
              ),
              entries: buildFinishedGarmentPreviewEntries(previewGarment),
            },
          ])
        : EMPTY_PRODUCTION_LIGHTBOX_DATA,
    [previewGarment],
  );
  const lightbox = useProductionLightbox({
    entries: lightboxData.entries,
    indexByKey: lightboxData.indexByKey,
    resetKey: previewGarment?.id,
  });
  const openLightboxAt = lightbox.openAt;

  const handleOpenPreview = useCallback(
    (kind: ProductionPreviewKind, sourceIndex: number) => {
      if (!previewGarment) return;
      openLightboxAt(productionPreviewKey(previewGarment.id, kind), sourceIndex);
    },
    [openLightboxAt, previewGarment],
  );

  // Amber "destination changed" band on the placement overlay — cleared at the
  // start of every new accept attempt, set only when the server's saved
  // destination differs from what was already being previewed.
  const [destinationChanged, setDestinationChanged] = useState(false);

  // Stable identities: useCatcherPlacement holds these in a ref internally, but
  // the inputs still need to be stable themselves so THIS effect doesn't churn
  // (PlacementOverlay's document keydown listener re-registers on onDismiss change).
  const handlePlacementPrePrint = useCallback(() => setShowPrePrintOverlay(true), []);
  const handlePlacementComplete = useCallback(({ destination, result }: AcceptedPlacement) => {
    clearGarment();
    const isBin = destination.variant === 'bin';
    const isBench = destination.variant === 'bench';
    const location = isBin || isBench ? destination.binLabel : t('catcher.packingBench');
    const progress = isBin || isBench
      ? t('garmentCard.caughtProgress', { caught: result.itemsCaught, total: result.totalItems })
      : '';
    const preposition = isBin ? t('catcher.caughtPlacedIn') : t('catcher.caughtPlacedOn');
    toast.success(
      result.orderComplete
        ? t('catcher.orderComplete', { location })
        : t('catcher.caughtPlaced', { preposition, location }),
      {
        description: [result.orderName, progress].filter(Boolean).join(' · '),
        duration: 4000,
      },
    );
  }, [clearGarment, t]);

  const {
    placement: pendingPlacement,
    isSaving: isPlacementSaving,
    isConfirmed: isPlacementConfirmed,
    begin: beginPlacement,
    confirm: handlePlacementDismiss,
    resolve: resolvePlacement,
    fail: failPlacement,
    reset: resetPlacement,
  } = useCatcherPlacement({
    onPrePrint: handlePlacementPrePrint,
    onComplete: handlePlacementComplete,
  });

  // Shared accept-outcome handling for handleAccept + handleBenchScanWithGarment:
  // beeps success only on a true completion, and only flags/beeps "changed" when
  // the server's destination differs from what was already being previewed —
  // a brand-new bin/shelf destination (no prior preview) is not a "change".
  const applyPlacementOutcome = useCallback((
    token: symbol,
    result: AcceptGarmentResult,
    isBench: boolean,
    preview: PlacementDestination | null,
  ) => {
    const outcome = resolvePlacement(token, result, isBench);
    if (outcome === 'completed') {
      if (!result.isPrePrintBatch) playSuccessBeep();
      return;
    }
    if (outcome === 'rearmed') {
      const finalDestination: PlacementDestination = result.binLabel
        ? { variant: isBench ? 'bench' : 'bin', binLabel: result.binLabel }
        : { variant: 'shelf', binLabel: '' };
      const changed = !!preview && (preview.variant !== finalDestination.variant || preview.binLabel !== finalDestination.binLabel);
      setDestinationChanged(changed);
      if (changed) playErrorBeep();
    }
  }, [resolvePlacement]);

  // Handle redo success - just clear garment
  const handleRedoSuccess = useCallback(() => {
    clearGarment();
  }, [clearGarment]);

  // Accept/Redo/Undo/AssignBench mutations
  const { acceptGarment, undoCatch, createRedo, assignBench, isAccepting, isUndoing, isCreatingRedo, isAssigningBench } =
    useCatcherMutations({
      onRedoSuccess: handleRedoSuccess,
    });

  // Stats query
  const { data: stats, isLoading: isLoadingStats } = useCatcherStats();

  // Recently caught (idle state)
  const { data: recentlyCaught } = useRecentlyCaught(selectedLocation?.id ?? null);

  // Redo reasons (configurable per-org) — evaluated at render time for i18n
  const DEFAULT_REDO_REASONS = [
    { value: 'print_defect', label: t('catcher.defaultReasons.printDefect'), description: t('catcher.defaultReasons.printDefectDesc') },
    { value: 'press_issue', label: t('catcher.defaultReasons.pressIssue'), description: t('catcher.defaultReasons.pressIssueDesc') },
    { value: 'stain_mark', label: t('catcher.defaultReasons.stainMark'), description: t('catcher.defaultReasons.stainMarkDesc') },
    { value: 'misprint', label: t('catcher.defaultReasons.misprint'), description: t('catcher.defaultReasons.misprintDesc') },
    { value: 'other', label: t('catcher.defaultReasons.other'), description: t('catcher.defaultReasons.otherDesc') },
  ];
  const redoReasonsQuery = trpc.productionSettings.getRedoReasons.useQuery();
  const redoReasons = redoReasonsQuery.data?.reasons ?? DEFAULT_REDO_REASONS;

  // Clear scanned garment and reset error tracking when location changes
  useEffect(() => {
    clearGarment();
    resetPlacement();
    setDestinationChanged(false);
    setShowPrePrintOverlay(false);
    hasScannedRef.current = false;
    shownErrorRef.current = null;
    lastQrCodeRef.current = null;
  }, [selectedLocation?.id, clearGarment, resetPlacement]);

  // Track if a scan was attempted to show error modals only on errors
  const hasScannedRef = useRef(false);
  // Track which error we've already shown to prevent infinite loops
  const shownErrorRef = useRef<string | null>(null);

  // Show error modal when lookup fails (success beep handled by QRScannerInput)
  // IMPORTANT: feedback is intentionally excluded from deps to prevent re-renders.
  // The feedback object is recreated every render (modalProps is not memoized),
  // so including it would cause this effect to fire on every render cycle.
  // The feedback methods (triggerRedoBlocked, triggerError) are stable via useCallback.
  useEffect(() => {
    if (!hasScannedRef.current) return;

    if (lookupError) {
      // Prevent showing the same error multiple times (fixes infinite beep loop)
      if (shownErrorRef.current === lookupError) return;
      shownErrorRef.current = lookupError;

      const parsed = parseRedoError(lookupError);
      const onHold = parseOrderOnHold(lookupError);
      const superseded = parseSupersededError(lookupError);
      if (superseded.isSuperseded) {
        feedback.triggerSuperseded(superseded.replacement);
      } else if (onHold.isOnHold) {
        feedback.triggerOrderOnHold(onHold.holdReason);
      } else if (parsed.isRedoError) {
        feedback.triggerRedoBlocked(parsed.replacement);
      } else if ('isInventoryError' in parsed && parsed.isInventoryError) {
        feedback.triggerStockIssue();
      } else if ('isOldLabel' in parsed && parsed.isOldLabel) {
        feedback.triggerWarning(
          t('catcher.oldLabel'),
          parsed.message || t('catcher.oldLabelDesc'),
          t('catcher.oldLabelAction'),
        );
      } else if ('isOrderItemRemoved' in parsed && parsed.isOrderItemRemoved) {
        feedback.triggerOrderItemRemoved();
      } else if ('isOrderChangedRemoved' in parsed && parsed.isOrderChangedRemoved) {
        feedback.triggerOrderChangedRemoved();
      } else if (parsed.isOrderCancelled) {
        feedback.triggerOrderCancelled();
      } else {
        feedback.triggerError(t('scanError'), getUserMessage(lookupError));
      }
    } else {
      // Clear shown error when lookup error clears
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupError]);

  // Note: loading state is shown inline (loading card) instead of a toast

  // USB scanners type characters as keyboard input - redirect to scanner field
  useScannerAutoFocus({
    paused: showRedoDialog || feedback.isModalOpen || isUndoing || !!pendingPlacement || showPrePrintOverlay || lightbox.open,
  });

  // Handle accept - always fires mutation immediately
  // Defined before handleShelfScanWithGarment because shelf auto-confirm calls it
  const handleAccept = useCallback(async () => {
    if (!garmentData || !selectedLocation?.id || garmentData.alreadyCaught ||
      isLookingUp || isUndoing || isCreatingRedo || isAssigningBench || feedback.isModalOpen) return;

    const garmentId = garmentData.garmentUnit.id;
    const directive = garmentData.binDirective;
    if (directive.action === 'ASSIGN_BENCH') return;
    const isBench = directive.action === 'PLACE_ON_BENCH';
    // Only preview an existing assignment. New bins and shelf/pre-print routing
    // remain server-authoritative; never guess a destination for the operator.
    const preview: PlacementDestination | null = directive.binId && directive.binLabel &&
        (directive.action === 'PLACE_IN_BIN' || isBench)
      ? { variant: isBench ? 'bench' : 'bin', binLabel: directive.binLabel }
      : null;
    const token = beginPlacement(preview);
    if (!token) return;
    setDestinationChanged(false);

    try {
      const result = await acceptGarment.mutateAsync({
        garmentUnitId: garmentId,
        binId: directive.binId ?? undefined,
        locationId: selectedLocation.id,
      });
      applyPlacementOutcome(token, result, isBench, preview);
    } catch (error) {
      // Roll back the preview before surfacing the error; keep the garment for
      // retry — but only if it's still the garment on screen. A stale token
      // (operator moved to a different garment/location) means the rollback
      // and the error modal both target a screen that's no longer live.
      const rolledBack = failPlacement(token);
      if (!rolledBack && garmentData?.garmentUnit.id !== garmentId) return;
      const parsed = parseRedoError(error);
      const onHold = parseOrderOnHold(error);
      const superseded = parseSupersededError(error);
      if (superseded.isSuperseded) {
        feedback.triggerSuperseded(superseded.replacement);
      } else if (onHold.isOnHold) {
        // Order paused (held, or pulled for missing stock) between scan and
        // accept: the same amber set-aside modal as the scan path.
        feedback.triggerOrderOnHold(onHold.holdReason);
      } else if (parsed.isRedoError) {
        feedback.triggerRedoBlocked(parsed.replacement);
      } else if ('isInventoryError' in parsed && parsed.isInventoryError) {
        feedback.triggerStockIssue();
      } else if ('isOldLabel' in parsed && parsed.isOldLabel) {
        feedback.triggerWarning(
          t('catcher.oldLabel'),
          parsed.message || t('catcher.oldLabelDesc'),
          t('catcher.oldLabelAction'),
        );
      } else if ('isOrderItemRemoved' in parsed && parsed.isOrderItemRemoved) {
        feedback.triggerOrderItemRemoved();
      } else if ('isOrderChangedRemoved' in parsed && parsed.isOrderChangedRemoved) {
        feedback.triggerOrderChangedRemoved();
      } else {
        feedback.triggerError(t('catcher.acceptError'), getUserMessage(error));
      }
    }
  }, [garmentData, acceptGarment, selectedLocation?.id, feedback, t, isLookingUp, isUndoing, isCreatingRedo, isAssigningBench, beginPlacement, applyPlacementOutcome, failPlacement]);

  // Handle shelf scan when a garment is already on screen (auto-confirm for single-line orders)
  // Defined before handleScan because handleScan references it
  const handleShelfScanWithGarment = useCallback(
    async (_qrCode: string) => {
      if (!garmentData) return;

      const binDirective = garmentData.binDirective;
      if (binDirective.action === 'PACKING_SHELF') {
        // Auto-trigger accept — single-line order, shelf scan confirms it
        await handleAccept();
      } else {
        // Not a single-line order — shelf scan doesn't make sense here
        feedback.triggerError(
          t('scanFeedback.wrongQr'),
          t('catcher.wrongQrMultiItem'),
        );
      }
    },
    [garmentData, handleAccept, feedback, t],
  );

  // BLU-197: handle a bench QR scan while a garment is on screen.
  // Only valid when the directive is ASSIGN_BENCH (order needs a container now).
  // Claims the scanned bench, then accepts the garment onto it — the post-accept
  // overlay then confirms physical placement on that same bench.
  const handleBenchScanWithGarment = useCallback(
    async (qrCode: string) => {
      if (!garmentData || !selectedLocation?.id) return;

      const directive = garmentData.binDirective;
      if (directive.action !== 'ASSIGN_BENCH') {
        // PLACE_ON_BENCH / bin / shelf orders don't claim a bench by scan here.
        feedback.triggerError(
          t('catcher.acceptFirst'),
          t('catcher.acceptFirstDesc'),
        );
        return;
      }

      const benchLabel = extractBenchLabel(qrCode);
      if (!benchLabel) {
        feedback.triggerError(t('qr.unrecognised'), t('qr.unrecognisedDesc'));
        return;
      }

      // Tracks whether the bench claim succeeded, so a failure in the SECOND
      // step (accept) is surfaced distinctly: the bench is now claimed for this
      // order, and the operator just needs to re-scan the garment (which then
      // resolves to PLACE_ON_BENCH) rather than re-scanning the bench (which
      // would return "bench in use" and dead-end them).
      const garmentId = garmentData.garmentUnit.id;
      const token = beginPlacement();
      if (!token) return;
      setDestinationChanged(false);
      let benchClaimed = false;
      try {
        // 1) Claim the bench for this order. Surfaces NOT_FOUND / "bench in use".
        const result = await assignBench.mutateAsync({
          garmentUnitId: garmentId,
          scannedBenchLabel: benchLabel,
          locationId: selectedLocation.id,
        });
        benchClaimed = true;
        // 2) Accept the garment onto the freshly-claimed bench.
        const accepted = await acceptGarment.mutateAsync({
          garmentUnitId: garmentId,
          binId: result.benchId,
          locationId: selectedLocation.id,
        });
        applyPlacementOutcome(token, accepted, true, null);
      } catch (error) {
        // See handleAccept: only skip the recovery/error feedback when the
        // operator has genuinely moved on to another garment/location.
        const rolledBack = failPlacement(token);
        if (!rolledBack && garmentData?.garmentUnit.id !== garmentId) return;
        // Claim succeeded but the garment accept failed → recoverable: the order
        // now owns the bench; re-scanning the garment continues via PLACE_ON_BENCH.
        if (benchClaimed) {
          feedback.triggerError(
            t('catcher.benchClaimedRescan'),
            t('catcher.benchClaimedRescanDesc'),
          );
          return;
        }
        const bench = parseBenchError(error);
        if (bench.isBenchNotFound) {
          feedback.triggerError(t('catcher.benchNotFound'), t('catcher.benchNotFoundDesc'));
        } else if (bench.isBenchInUse) {
          feedback.triggerError(t('catcher.benchInUse'), t('catcher.benchInUseDesc'));
        } else {
          feedback.triggerError(t('catcher.benchAssignError'), getUserMessage(error));
        }
      }
    },
    [garmentData, selectedLocation?.id, assignBench, acceptGarment, feedback, t, beginPlacement, applyPlacementOutcome, failPlacement],
  );

  // Handle QR scan — detect bin/bench/shelf QR types before sending to backend
  const handleScan = useCallback(
    (qrCode: string) => {
      hasScannedRef.current = true;

      // Detect QR type before sending to backend
      const qrType = detectQRType(qrCode);

      if (qrType === 'bench') {
        if (garmentData) {
          // Garment on screen — delegate to bench handler (validates directive).
          handleBenchScanWithGarment(qrCode);
        } else {
          feedback.triggerError(
            t('scanFeedback.wrongQr'),
            t('catcher.wrongQrBench'),
          );
        }
        return;
      }

      if (qrType === 'bin') {
        if (garmentData) {
          // Garment already on screen — user scanned bin before clicking Accept
          feedback.triggerError(
            t('catcher.acceptFirst'),
            t('catcher.acceptFirstDesc'),
          );
        } else {
          // No garment on screen — tell user to scan garment first
          feedback.triggerError(
            t('scanFeedback.wrongQr'),
            t('catcher.wrongQrBin'),
          );
        }
        return;
      }

      if (qrType === 'shelf') {
        if (garmentData) {
          // Garment on screen — delegate to shelf handler
          handleShelfScanWithGarment(qrCode);
        } else {
          feedback.triggerError(
            t('scanFeedback.wrongQr'),
            t('catcher.wrongQrShelf'),
          );
        }
        return;
      }

      if (qrType === 'unknown') {
        feedback.triggerError(
          t('qr.unrecognised'),
          t('qr.unrecognisedDesc'),
        );
        return;
      }

      // BLU-280: If same garment is already displayed, show toast instead of re-fetching
      if (garmentData) {
        const incomingId = extractGarmentId(qrCode);
        if (incomingId && incomingId === garmentData.garmentUnit.id) {
          toast.info(t('catcher.alreadyViewing'), { duration: 2000 });
          return;
        }
      }

      // Normal garment QR — proceed with lookup
      lastQrCodeRef.current = qrCode;
      lookupGarment(qrCode);
    },
    [lookupGarment, garmentData, feedback, handleShelfScanWithGarment, handleBenchScanWithGarment, t],
  );

  // Handle redo click
  const handleRedoClick = useCallback(() => {
    setShowRedoDialog(true);
  }, []);

  // BLU-280: Ref-based guard to prevent double-click/double-Enter on redo confirm
  const isRedoingRef = useRef(false);

  // Handle redo confirm with type-safe reason
  const handleRedoConfirm = useCallback(
    async (reason: RedoReason) => {
      if (!garmentData || !selectedLocation?.id) return;
      if (isRedoingRef.current) return;
      isRedoingRef.current = true;

      try {
        await createRedo.mutateAsync({
          garmentUnitId: garmentData.garmentUnit.id,
          reason,
          locationId: selectedLocation.id,
        });

        setShowRedoDialog(false);
      } catch (error) {
        feedback.triggerError(
          t('catcher.redoError'),
          getUserMessage(error),
          t('catcher.redoErrorDesc')
        );
      } finally {
        isRedoingRef.current = false;
      }
    },
    [garmentData, createRedo, selectedLocation?.id, feedback, t],
  );

  // Ref-based guard to prevent double-click on undo (same pattern as isDismissingRef in PlacementOverlay)
  const isUndoingRef = useRef(false);

  // Reset undo/redo guards when mutations settle
  useEffect(() => {
    if (!isUndoing) {
      isUndoingRef.current = false;
    }
    if (!isCreatingRedo) {
      isRedoingRef.current = false;
    }
  }, [isUndoing, isCreatingRedo]);

  // Handle undo from overlay (after accept, before placing in bin)
  const handleOverlayUndo = useCallback(async () => {
    if (!garmentData || !selectedLocation?.id) return;
    if (isUndoingRef.current) return;
    isUndoingRef.current = true;
    try {
      await undoCatch.mutateAsync({
        garmentUnitId: garmentData.garmentUnit.id,
        locationId: selectedLocation.id,
      });
      // Success: close overlay, re-fetch garment in PICKED status
      resetPlacement();
      playSuccessBeep();
      if (lastQrCodeRef.current) lookupGarment(lastQrCodeRef.current);
    } catch (error) {
      // Failure: keep overlay open, show inline error via feedback
      feedback.triggerError(t('catcher.undoError'), getUserMessage(error));
    }
  }, [garmentData, selectedLocation?.id, undoCatch, lookupGarment, feedback, t, resetPlacement]);

  // Handle undo from re-scan (garment already caught)
  const handleUndoCatch = useCallback(async () => {
    if (!garmentData || !selectedLocation?.id) return;
    if (isUndoingRef.current) return;
    isUndoingRef.current = true;
    try {
      await undoCatch.mutateAsync({
        garmentUnitId: garmentData.garmentUnit.id,
        locationId: selectedLocation.id,
      });
      playSuccessBeep();
      if (lastQrCodeRef.current) lookupGarment(lastQrCodeRef.current);
    } catch (error) {
      feedback.triggerError(t('catcher.undoError'), getUserMessage(error));
    }
  }, [garmentData, selectedLocation?.id, undoCatch, lookupGarment, feedback, t]);

  const hasActiveGarment = !!garmentData;

  // Configurable hotkeys via useHotkeyEngine (BLU-289)
  const { hotkeyMap } = useHotkeySettings();
  const hotkeyHandlers = useMemo(() => {
    if (!hasActiveGarment) return {};
    return {
      catcher_primary: () => {
        if (garmentData?.alreadyCaught) {
          handleUndoCatch();
        } else if (garmentData?.binDirective.action === 'ASSIGN_BENCH') {
          // BLU-197: bench orders are confirmed by scanning a bench, not a hotkey.
          return;
        } else {
          handleAccept();
        }
      },
      ...(!garmentData?.alreadyCaught && {
        catcher_redo: () => handleRedoClick(),
      }),
      catcher_back: () => clearGarment(),
    } as Partial<Record<import('@threadheads/shared').HotkeyAction, () => void>>;
  }, [hasActiveGarment, garmentData?.alreadyCaught, garmentData?.binDirective.action, handleAccept, handleUndoCatch, handleRedoClick, clearGarment]);

  useHotkeyEngine(hotkeyMap, hotkeyHandlers, {
    paused: showRedoDialog || feedback.isModalOpen || !!pendingPlacement || showPrePrintOverlay || isPlacementSaving || lightbox.open,
  });

  // Location guard
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {t('catcher.selectLocation')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isBusy =
    isLookingUp ||
    isAccepting ||
    isPlacementSaving ||
    isUndoing ||
    isCreatingRedo ||
    isAssigningBench ||
    !!pendingPlacement ||
    showPrePrintOverlay ||
    feedback.isModalOpen;

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      {/* ARIA live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isLookingUp && t('lookingUp')}
        {garmentData &&
          t('garmentFound', { product: garmentData.garmentUnit.productName, size: garmentData.garmentUnit.size || 'unknown' })}
        {lookupError && `${t('scanFeedback.error')}${lookupError}`}
      </div>

      {/* Page header with icon box pattern */}
      <div className="flex items-center gap-3">
        {hasActiveGarment && (
          <Button
            variant="ghost"
            onClick={clearGarment}
            disabled={isBusy}
            className="shrink-0 gap-1.5"
            aria-label={t('backTo', { station: t('catcher.title') })}
          >
            <ArrowLeft className="h-5 w-5" />
            <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.catcher_back)}</kbd>
          </Button>
        )}
        <div className="p-2 rounded-lg bg-primary/10">
          <Shirt className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('catcher.title')}</h1>
          <p className="text-muted-foreground">
            {t('catcher.description')}
          </p>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Scrollable content area */}
      <div className="flex-1 space-y-6">
        {/* ACTIVE state: garment info at top, scanner hidden */}
        {hasActiveGarment && (
          <>
            <GarmentCard
              garment={garmentData.garmentUnit}
              order={garmentData.order}
              alreadyCaught={garmentData.alreadyCaught}
              redoReasons={redoReasons}
              onOpenPreview={handleOpenPreview}
            />
            {/* BLU-197: bench directive prompt — only when bench routing is active
                for this order (backend emits these actions only when enabled, so
                this whole branch is dark by default). ASSIGN_BENCH tells the
                operator to scan a bench; PLACE_ON_BENCH previews the bench. */}
            {!garmentData.alreadyCaught &&
              (garmentData.binDirective.action === 'ASSIGN_BENCH' ||
                garmentData.binDirective.action === 'PLACE_ON_BENCH') && (
                <BinDirective directive={garmentData.binDirective} isAssigning={isAssigningBench} />
              )}
            {/* Hidden scanner input — still captures USB scanner keystrokes
                and handles shelf QR auto-accept for single-line orders */}
            <div className="sr-only">
              <QRScannerInput
                onScan={handleScan}
                disabled={isBusy}
                isLoading={isLookingUp}
              />
            </div>
          </>
        )}

        {/* Loading state: shown while looking up a garment (replaces idle content) */}
        {!hasActiveGarment && isLookingUp && (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <p className="text-lg text-muted-foreground">{t('loadingItem')}</p>
            </CardContent>
          </Card>
        )}

        {/* IDLE state: visible scanner card */}
        {!hasActiveGarment && !isLookingUp && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('scanGarment')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <QRScannerInput
                onScan={handleScan}
                disabled={isBusy}
                isLoading={isLookingUp}
              />
            </CardContent>
          </Card>
        )}

        {/* Stats row - hidden when garment is active or loading, shown in idle state */}
        {!hasActiveGarment && !isLookingUp && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isLoadingStats ? (
              <>
                <Skeleton className="h-24 rounded-xl" />
                <Skeleton className="h-24 rounded-xl" />
                <Skeleton className="h-24 rounded-xl" />
              </>
            ) : (
              <>
                <MetricCard
                  variant="production"
                  title={t('catcher.caughtToday')}
                  value={String(stats?.caughtToday ?? 0)}
                  icon={Package}
                />
                <MetricCard
                  variant={stats?.redosToday ? 'warning' : 'gst'}
                  title={t('catcher.redosToday')}
                  value={String(stats?.redosToday ?? 0)}
                  subtitle={
                    stats?.redosToday ? t('catcher.itemsNeedReprinting') : t('catcher.noIssues')
                  }
                  icon={RefreshCw}
                />
                <MetricCard
                  variant="info"
                  title={t('catcher.pendingInBatch')}
                  value={String(stats?.pendingInBatch ?? 0)}
                  icon={Clock}
                />
              </>
            )}
          </div>
        )}

        {/* Recently Caught (idle state, hidden during loading) */}
        {!hasActiveGarment && !isLookingUp && recentlyCaught?.items && recentlyCaught.items.length > 0 && (
          <RecentlyCaughtList
            items={recentlyCaught.items}
            t={t}
            onSelectGarment={(garmentId) => {
              const payload = JSON.stringify({ garment_unit_id: garmentId });
              const encoded = btoa(payload);
              lookupGarment(`THP://${encoded}`);
            }}
          />
        )}

        {/* Dev-only QR test panel */}
        <DevQRTestPanel />
      </div>

      {/* Sticky action buttons at bottom when garment is active */}
      {hasActiveGarment && (
        <div className="sticky bottom-0 z-10 bg-background pb-4 pt-4 border-t">
          {garmentData.alreadyCaught ? (
            /* Already-caught garment: show only Undo Catch button */
            <Button
              size="lg"
              className="w-full h-16 text-xl font-semibold bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleUndoCatch}
              disabled={isUndoing}
            >
              <Undo2 className="h-5 w-5 mr-2" />
              {isUndoing ? t('undoing') : t('undoCatch')}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.catcher_primary)}</kbd>
            </Button>
          ) : (
            /* Normal garment: Accept / Redo buttons.
               BLU-197: for ASSIGN_BENCH the operator scans a bench instead of
               clicking Accept — replace the Accept CTA with a scan prompt. */
            <div className="flex gap-4">
              {garmentData.binDirective.action === 'ASSIGN_BENCH' ? (
                <div
                  className="flex-1 h-16 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 text-xl font-semibold"
                  role="status"
                  aria-live="polite"
                >
                  {isAssigningBench ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('catcher.assigningBench')}
                    </>
                  ) : (
                    <>
                      <ScanLine className="h-5 w-5" />
                      {t('catcher.scanBenchToContinue')}
                    </>
                  )}
                </div>
              ) : (
                <Button
                  size="lg"
                  className="flex-1 h-16 text-xl font-semibold"
                  onClick={handleAccept}
                  disabled={isAccepting || isCreatingRedo || isUndoing}
                >
                  {isAccepting ? t('accepting') : t('accept')}
                  <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.catcher_primary)}</kbd>
                </Button>
              )}
              <Button
                size="lg"
                variant="destructive"
                className="flex-1 h-16 text-xl font-semibold"
                onClick={handleRedoClick}
                disabled={isAccepting || isCreatingRedo || isUndoing || isAssigningBench}
              >
                {t('redo')}
                <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.catcher_redo)}</kbd>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Redo dialog */}
      <RedoReasonDialog
        open={showRedoDialog}
        onOpenChange={setShowRedoDialog}
        onConfirm={handleRedoConfirm}
        isPending={isCreatingRedo}
        reasons={redoReasons}
        reasonsLoading={redoReasonsQuery.isLoading}
        copy={{
          selectTitle: t('catcher.selectRedoReason'),
          confirmTitle: t('catcher.confirmRedo'),
          confirmButtonLabel: t('catcher.confirmRedo'),
          pendingLabel: t('catcher.creatingRedo'),
          loadingReasonsLabel: t('catcher.loadingReasons'),
          confirmAriaLabel: (reason) => t('catcher.confirmRedoFor', { reason }),
          goBackAriaLabel: t('catcher.goBackToReasons'),
        }}
      />

      {/* Existing assignments preview immediately; completion waits for accept. */}
      <PlacementOverlay
        allowManualEnter
        paused={feedback.isModalOpen}
        open={!!pendingPlacement}
        variant={pendingPlacement?.variant ?? 'bin'}
        binLabel={pendingPlacement?.binLabel ?? ''}
        onDismiss={handlePlacementDismiss}
        onUndo={handleOverlayUndo}
        isUndoing={isUndoing}
        isSaving={isPlacementSaving}
        isConfirmed={isPlacementConfirmed}
        destinationChanged={destinationChanged}
      />

      {/* Pre-print overlay (full-screen violet, shown instead of placement for pre-print batches) */}
      <PrePrintOverlay
        open={showPrePrintOverlay}
        productName={garmentData?.garmentUnit.productName ?? ''}
        sku={garmentData?.garmentUnit.sku ?? ''}
        imageUrl={garmentData?.garmentUnit.imageUrl ?? null}
        onDismiss={() => {
          setShowPrePrintOverlay(false);
          clearGarment();
        }}
      />

      {/* Garment image lightbox — arrows and arrow keys flick between this
          garment's sides; closing hands the scanner back (§6.2). */}
      <ProductionImageLightbox
        open={lightbox.open}
        onOpenChange={lightbox.setOpen}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        copy={{
          title: t('catcher.preview.lightboxTitle'),
          description: t('catcher.preview.lightboxDescription'),
          previousLabel: t('preview.previousImage'),
          nextLabel: t('preview.nextImage'),
        }}
      />

      {/* Scan error modal */}
      <ScanErrorModal {...feedback.modalProps} />

      {/* Superseded-sticker dialog (amber; one-tap reprint of the live replacement) */}
      <SupersededStickerDialog
        {...feedback.supersededProps}
        locationId={selectedLocation?.id ?? null}
        currentStage="Catching"
      />
    </div>
  );
}

// ============================================================================
// RecentlyCaughtList — Recently caught garments for idle state
// ============================================================================

function formatTimeAgo(isoString: string, t: TFunction): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t('common:time.justNow');
  if (minutes < 60) return t('common:time.minutesAgo_other', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common:time.hoursAgo_other', { count: hours });
  return t('common:time.daysAgo_other', { count: Math.floor(hours / 24) });
}

function statusLabel(status: string, binLabel: string | null, t: TFunction): string {
  switch (status) {
    case 'CAUGHT': return t('garmentCard.shelf');
    case 'IN_BIN': return binLabel ?? 'Bin';
    case 'READY_TO_PACK': return t('station.ready');
    case 'PACKED': return t('packer.packed');
    case 'SHIPPED': return 'Shipped';
    case 'ON_SHELF': return t('garmentCard.shelf');
    default: return status;
  }
}

interface RecentlyCaughtListProps {
  items: {
    id: string;
    sku: string;
    productName: string;
    size: string | null;
    color: string | null;
    orderName: string;
    isRedo: boolean;
    redoReason: string | null;
    status: string;
    binLabel: string | null;
    caughtAt: string;
  }[];
  t: TFunction;
  onSelectGarment: (garmentId: string) => void;
}

function RecentlyCaughtList({ items, t, onSelectGarment }: RecentlyCaughtListProps) {
  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-sm font-medium text-muted-foreground">{t('recentlyCaught')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-2 pt-0">
        <div className="divide-y">
          {items.map((item) => (
            <button
              key={item.id}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
              onClick={() => onSelectGarment(item.id)}
            >
              <span className="font-medium text-sm min-w-0 truncate flex-1">{item.productName}</span>
              {item.isRedo && (
                item.redoReason === 'order_change_backfill' ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300">
                    <Plus className="h-2.5 w-2.5 mr-0.5" />
                    {t('orderChange.badge')}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0 shrink-0">
                    <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                    REDO
                  </Badge>
                )
              )}
              {item.size && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  {item.size}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs shrink-0">
                {statusLabel(item.status, item.binLabel, t)}
              </Badge>
              <span className="text-sm text-muted-foreground shrink-0">{formatOrderNumber(item.orderName)}</span>
              <span className="text-xs text-muted-foreground shrink-0 w-14 text-right">
                {formatTimeAgo(item.caughtAt, t)}
              </span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
